import React from 'react';
import FillHandle from '../spreadsheet/FillHandle';
import useFillDrag from '../spreadsheet/useFillDrag';

/**
 * The selection's box in the grid's content coordinates, where row i starts
 * at headerHeight + i * rowHeight: the space FillHandle draws in. Pinned
 * columns stick to the left edge, so while the grid is scrolled sideways
 * they sit `scrollLeft` further right than their place in the layout, and
 * scrolled columns slide under them. Null when the selection's right edge is
 * hidden under the pinned columns.
 */
export function fillSelectionRect(bounds, columnLayout, { headerHeight, rowHeight, scrollLeft = 0 }) {
    const first = columnLayout[bounds.minColumnIndex];
    const last = columnLayout[bounds.maxColumnIndex];
    if (!first || !last) return null;
    const pinnedEdge = scrollLeft + columnLayout
        .filter((entry) => entry.pinned)
        .reduce((total, entry) => total + entry.width, 0);
    const left = first.pinned ? scrollLeft + first.offset : Math.max(first.offset, pinnedEdge);
    const right = (last.pinned ? scrollLeft : 0) + last.offset + last.width;
    if (!last.pinned && right <= pinnedEdge) return null;
    return {
        left,
        top: headerHeight + (bounds.minRowIndex * rowHeight),
        width: Math.max(0, right - left),
        height: (bounds.maxRowIndex - bounds.minRowIndex + 1) * rowHeight,
    };
}

function boundsKey(bounds) {
    return bounds
        ? `${bounds.minRowIndex}:${bounds.maxRowIndex}:${bounds.minColumnIndex}:${bounds.maxColumnIndex}`
        : '';
}

function boundsFromKey(key) {
    if (!key) return null;
    const [minRowIndex, maxRowIndex, minColumnIndex, maxColumnIndex] = key.split(':').map(Number);
    return { minRowIndex, maxRowIndex, minColumnIndex, maxColumnIndex };
}

function FillHandleForSelection({
    bounds,
    fill,
    onFillPreviewChange,
    columnLayout,
    scrollLeft,
    headerHeight,
    rowHeight,
    rowCount,
    getScrollElement,
}) {
    const sourceRange = bounds ? { startIndex: bounds.minRowIndex, endIndex: bounds.maxRowIndex } : null;
    const drag = useFillDrag({
        getScrollElement,
        headerHeight,
        rowHeight,
        rowCount,
        sourceRange,
        onPreview: (targetRange, source) => {
            onFillPreviewChange(targetRange ? fill.preview(source, targetRange) : null);
        },
        onCommit: (targetRange, source) => fill.commit(source, targetRange),
        onFillToEnd: (source) => fill.fillToEnd(source),
    });
    // The handle is for ranges a fill can write into.
    const selectionRect = bounds && fill.canFill(bounds)
        ? fillSelectionRect(bounds, columnLayout, { headerHeight, rowHeight, scrollLeft })
        : null;
    if (!selectionRect) return null;
    return (
        <FillHandle
            selectionRect={selectionRect}
            handleProps={drag.handleProps}
            targetRange={drag.targetRange}
            headerHeight={headerHeight}
            rowHeight={rowHeight}
        />
    );
}

/**
 * The spreadsheet fill handle on the corner of the grid's selection, drawn
 * inside the scrolled content element. It follows the selection through the
 * table's cell selection atom and shows only for one range that holds an
 * editable cell, while no editor is open (`disabled`). `fill` is the editing
 * layer's fill API; `onFillPreviewChange` publishes the drag's preview to
 * the rows.
 */
function WatchlistGridFillHandle({ selection, getBounds, disabled, ...props }) {
    const selectBoundsKey = (ranges) => (
        disabled || ranges?.length !== 1 ? '' : boundsKey(getBounds())
    );
    return (
        <selection.Subscribe source={selection.atom} selector={selectBoundsKey}>
            {(key) => <FillHandleForSelection bounds={boundsFromKey(key)} {...props} />}
        </selection.Subscribe>
    );
}

export default React.memo(WatchlistGridFillHandle);
